import React, {useState, useCallback, useMemo} from 'react';
import {Input, Calendar, Grid, GridCol} from '@flixbus/honeycomb-react';
import {
  Icon,
  IconCalendarSolid,
  IconClose
} from '@flixbus/honeycomb-icons-react';
import {useDocumentEvent} from '@/Pages/Common/hooks/useDocumentEvent';
import {getPlanningPeriodDate} from '@/Pages/ProductionCost/utils/formatePlanningPeriodDate';
import {updateFilter} from '@/Pages/Home/store/slice';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import styles from './PlanningPeriod.module.scss';

const firstDate = new Date(2019, 0, 1);
const lastYear = new Date().getFullYear() + 10;
const lastDate = new Date(lastYear, 11, 31);

const PlanningPeriod: React.FC = () => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const dispatch = useAppDispatch();

  const handleEvent = e => {
    if (e.type === 'click' || e.key === 'Escape') setShowCalendar(false);
  };

  useDocumentEvent([
    {type: 'click', callback: handleEvent},
    {type: 'keydown', callback: handleEvent}
  ]);

  const updateStore = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      const payload = {
        field: 'planningPeriods',
        value: {
          startDate: startDate?.toISOString() ?? null,
          endDate: endDate?.toISOString() ?? null
        }
      };
      dispatch(updateFilter(payload));
    },
    [dispatch]
  );

  const clearFilter = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    updateStore(null, null);
  }, [updateStore]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      const isStartDateSelection = !startDate;
      const isEndDateSelection = !endDate;

      // date range is already selected and user wants to change the start date
      const isConsecutiveSelection = !!endDate;

      if (isStartDateSelection) {
        setStartDate(date);
      } else if (isEndDateSelection) {
        setEndDate(date);
        updateStore(startDate, date);
        setShowCalendar(false);
      } else if (isConsecutiveSelection) {
        setStartDate(date);
        setEndDate(null);
      }
    },
    [startDate, endDate, updateStore]
  );

  const planningPeriodValue = useMemo(() => {
    return getPlanningPeriodDate(
      startDate ? startDate.toString() : null,
      endDate ? endDate.toString() : null
    );
  }, [endDate, startDate]);

  return (
    <div style={{position: 'relative'}} onClick={e => e.stopPropagation()}>
      <Grid>
        <GridCol>
          <Input
            iconLeft={
              <Icon
                InlineIcon={IconCalendarSolid}
                aria-label={'Calendar icon'}
              />
            }
            label="Planning period"
            id="planning-period-input"
            onFocus={() => setShowCalendar(true)}
            value={planningPeriodValue}
            readOnly
          />
          {endDate && (
            <Icon
              InlineIcon={IconClose}
              className={styles.clearSelectionIcon}
              onClick={clearFilter}
              aria-label={'Clear selection'}
            />
          )}
        </GridCol>
      </Grid>

      <Calendar
        appearance={'birthday'}
        hidden={!showCalendar}
        extraClasses={'planning-period-calendar'}
        id="home-page-planning-period-calender"
        startDate={firstDate}
        endDate={lastDate}
        defaultMonth={startDate ?? new Date()}
        startSelected={startDate ?? undefined}
        endSelected={endDate ?? undefined}
        handleSelect={handleDateSelect}
        disableDayCondition={day =>
          startDate && !endDate ? day < startDate : false
        }
      />
    </div>
  );
};

export default React.memo(PlanningPeriod);
